import { useForm } from "@tellescope/forms";
import { useTellescopeMutation, useVitalsQueryWithFilters } from "@tellescope/tanstack-query";
import { Button, Checkbox, Combobox, DateTimePicker, Label, NumberInput } from "@tellescope/ui";
import type { ComboboxOption } from "@tellescope/ui";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface AddOtherReadingSubsectionProps {
  enduserId: string;
  onSubmit: () => void;
  onCancel: () => void;
}

export const AddOtherReadingSubsection = ({ enduserId, onSubmit, onCancel }: AddOtherReadingSubsectionProps) => {
  const { t } = useTranslation("vitals");
  const initialTimestampRef = useRef(new Date());

  const createObservationMutation = useTellescopeMutation({
    model: "enduser_observations",
    method: "createOne",
    onSuccess: onSubmit,
  });

  const form = useForm({
    fields: {
      timestamp: {
        type: "dateTime",
        initialValue: initialTimestampRef.current,
      },
      estimatedTime: {
        type: "checkbox",
        initialValue: false,
      },
      value: {
        type: "number",
        required: true,
        initialValue: undefined,
      },
      unit: {
        type: "select",
        required: true,
        initialValue: undefined,
      },
    },
    validateOn: "change",
    onSubmit: (values) => {
      createObservationMutation.mutate({
        category: "vital-signs",
        enduserId,
        measurement: {
          unit: values.unit,
          value: values.value,
        },
        recordedAt: values.timestamp,
        source: "tellescope-2-provider-webapp",
        status: "final",
        timestamp: values.timestamp,
        timestampIsEstimated: values.estimatedTime,
      });
    },
  });

  const { data: observations } = useVitalsQueryWithFilters({
    filters: { enduserIds: [enduserId], validStatus: "valid" },
    combineRelatedObservations: false,
  });

  const [unitSearchQuery, setUnitSearchQuery] = useState("");

  const unitOptions = useMemo<ComboboxOption[]>(() => {
    const countsByUnit = (observations ?? []).reduce<Record<string, number>>((acc, { measurements }) => {
      measurements.forEach(({ unit }) => {
        acc[unit] = (acc[unit] ?? 0) + 1;
      });
      return acc;
    }, {});

    return Object.entries(countsByUnit)
      .sort(([, a], [, b]) => b - a) // show most used units first
      .map(([unit]) => ({ value: unit, label: unit }));
  }, [observations]);

  const unitValue = useMemo<ComboboxOption | undefined>(() => {
    const unit = form.values.unit;
    if (!unit || typeof unit !== "string") return undefined;
    return { value: unit, label: unit };
  }, [form.values.unit]);

  const unitFilter = useCallback(
    (searchTerm: string, options: ComboboxOption[]) => {
      const lowerSearch = searchTerm.toLowerCase().trim();
      if (!lowerSearch) return options;

      const filtered = options.filter((option) => option.label.toLowerCase().includes(lowerSearch));
      const exactMatch = options.some((option) => option.label.toLowerCase() === lowerSearch);

      if (!exactMatch) {
        filtered.push({
          value: searchTerm.trim(),
          label: t('Create "{{unit}}"', { unit: searchTerm.trim() }),
        });
      }

      return filtered;
    },
    [t]
  );

  return (
    <form className="flex flex-col gap-6" {...form.getFormProps()}>
      <DateTimePicker {...form.getCustomFieldProps("timestamp")} className="w-fit" label={t("Date & Time")} />
      <span className="flex items-center gap-2">
        <Checkbox
          id="estimated-time"
          checked={form.values.estimatedTime}
          onCheckedChange={(checked) => form.setFieldValue("estimatedTime", checked !== false)}
        />
        <Label htmlFor="estimated-time" className="font-normal cursor-pointer">
          {t("Time is estimated")}
        </Label>
      </span>
      <div className="space-y-3">
        <Label htmlFor="value" required>
          {t("Value")}
        </Label>
        <NumberInput
          id="value"
          placeholder={t("e.g. 98.6")}
          {...form.getFieldProps("value")}
          onChange={(e) => form.setFieldValue("value", e ?? 0)}
        />
      </div>
      <div className="space-y-3">
        <Label required>{t("Unit")}</Label>
        <Combobox
          value={unitValue}
          onChange={(option) => {
            form.setFieldValue("unit", option?.value ?? "");
            setUnitSearchQuery("");
          }}
          options={unitOptions}
          customFilter={unitFilter}
          placeholder={t("e.g. °F, mg/dL, lbs")}
          searchPlaceholder={t("Search or type a unit...")}
          emptyText={t("Type to create a new unit")}
          searchQuery={unitSearchQuery}
          setSearchQuery={setUnitSearchQuery}
          clearable
        />
      </div>
      <div className="w-full grid grid-cols-2 gap-2">
        <Button variant="outline" onClick={onCancel}>
          {t("Cancel")}
        </Button>
        <Button disabled={!form.isValid} type="submit" loading={createObservationMutation.isPending}>
          {t("Save reading")}
        </Button>
      </div>
    </form>
  );
};
